(() => {
  const HEADER_SELECTOR = '[data-lucien-header]';
  const LOCK_CLASS = 'lucien-menu-lock';
  const FOOTER_EDGE_DISTANCE = 120;
  const BOUNCE_EDGE_DISTANCE = 80;
  const STATE_CHANGE_COOLDOWN = 300;
  const SCROLL_THRESHOLDS = {
    mobile: {
      hide: 48,
      reveal: 150,
      footerReveal: 180,
    },
    desktop: {
      hide: 30,
      reveal: 96,
      footerReveal: 180,
    },
  };
  let lockedScrollY = 0;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const isDebugEnabled = () => {
    if (window.LUCIEN_SCROLL_DEBUG === true) return true;

    try {
      return new URLSearchParams(window.location.search).get('lucien_scroll_debug') === '1';
    } catch (error) {
      return false;
    }
  };

  const debug = (...args) => {
    if (!isDebugEnabled() || !window.console || typeof window.console.info !== 'function') return;

    window.console.info('[lucien-scroll]', ...args);
  };

  const isMobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

  const getMaxScrollY = () => {
    const documentElement = document.documentElement;
    return Math.max(documentElement.scrollHeight - window.innerHeight, 0);
  };

  const getClampedScrollY = () => {
    const rawScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    return Math.min(Math.max(rawScrollY, 0), getMaxScrollY());
  };

  const getNow = () => {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
  };

  const setScrollLock = (isLocked) => {
    const isAlreadyLocked = document.documentElement.classList.contains(LOCK_CLASS);

    if (isLocked && isAlreadyLocked) return;
    if (!isLocked && !isAlreadyLocked) return;

    if (isLocked) {
      lockedScrollY = getClampedScrollY();
      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
    }

    document.documentElement.classList.toggle(LOCK_CLASS, isLocked);
    document.body.classList.toggle(LOCK_CLASS, isLocked);

    if (!isLocked) {
      window.scrollTo(0, Math.min(lockedScrollY, getMaxScrollY()));
      lockedScrollY = 0;
    }

    debug(isLocked ? 'body-lock' : 'body-unlock');
  };

  const getFocusable = (container) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      return element.offsetParent !== null || element === document.activeElement;
    });
  };

  const initHeader = (root) => {
    if (!root || root.dataset.lucienInitialized === 'true') return;

    const menu = root.querySelector('[data-lucien-menu]');
    const panel = root.querySelector('[data-lucien-menu-panel]');
    const toggle = root.querySelector('[data-lucien-menu-toggle]');
    const closeControls = root.querySelectorAll('[data-lucien-menu-close]');
    const menuLinks = root.querySelectorAll('[data-lucien-menu-link]');

    if (!menu || !panel || !toggle) return;

    root.dataset.lucienInitialized = 'true';

    const hideOnScroll = root.dataset.hideOnScroll !== 'false';
    const topThreshold = toNumber(root.dataset.topThreshold, 8);
    const revealOffset = toNumber(root.dataset.revealOffset, 70);
    const scrollTolerance = toNumber(root.dataset.scrollTolerance, 6);
    let lastScrollY = getClampedScrollY();
    let isTicking = false;
    let isMenuOpen = false;
    let lastActiveElement = null;
    let scrollDirection = 0;
    let directionDistance = 0;
    let isHidden = root.classList.contains('is-hidden');
    let lastStateChangeTime = 0;
    let directionStartedNearBottom = false;

    if ('inert' in menu) {
      menu.inert = true;
    }

    const getScrollMetrics = () => {
      const maxScrollY = getMaxScrollY();
      const rawScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      const currentY = Math.min(Math.max(rawScrollY, 0), maxScrollY);
      const distanceFromBottom = Math.max(maxScrollY - currentY, 0);

      return {
        maxScrollY,
        currentY,
        distanceFromBottom,
        isNearBottom: distanceFromBottom < FOOTER_EDGE_DISTANCE,
        isAtBottomEdge: maxScrollY > 0 && distanceFromBottom <= BOUNCE_EDGE_DISTANCE,
      };
    };

    const getThresholds = () => {
      return isMobileViewport() ? SCROLL_THRESHOLDS.mobile : SCROLL_THRESHOLDS.desktop;
    };

    const resetDirectionTracking = () => {
      scrollDirection = 0;
      directionDistance = 0;
      directionStartedNearBottom = false;
    };

    const showTopState = (currentY, now) => {
      const wasHidden = isHidden || root.classList.contains('is-hidden');
      const wasAtTop = root.classList.contains('is-at-top') && !root.classList.contains('is-filled');

      if (!wasAtTop || wasHidden) {
        root.classList.remove('is-filled', 'is-hidden');
        root.classList.add('is-at-top');
        debug('header-state', 'top');
      }

      isHidden = false;

      if (wasHidden) {
        lastStateChangeTime = now;
      }

      resetDirectionTracking();
      lastScrollY = currentY;
    };

    const showFilledState = () => {
      if (root.classList.contains('is-filled') && !root.classList.contains('is-at-top')) {
        return;
      }

      root.classList.remove('is-at-top');
      root.classList.add('is-filled');
      debug('header-state', 'filled');
    };

    const hideHeader = (now) => {
      if (isHidden) return;

      isHidden = true;
      root.classList.add('is-hidden', 'is-filled');
      root.classList.remove('is-at-top');
      lastStateChangeTime = now;
      directionDistance = 0;
      debug('header-state', 'hidden');
    };

    const showHeader = (now) => {
      if (!isHidden) return;

      isHidden = false;
      root.classList.remove('is-hidden', 'is-at-top');
      root.classList.add('is-filled');
      lastStateChangeTime = now;
      directionDistance = 0;
      debug('header-state', 'shown');
    };

    const syncScrollState = ({ forceVisible = false } = {}) => {
      const { currentY, isAtBottomEdge } = getScrollMetrics();
      const now = getNow();

      if (isMenuOpen) {
        lastScrollY = currentY;
        return;
      }

      if (currentY <= topThreshold) {
        showTopState(currentY, now);
        return;
      }

      showFilledState();

      if (isAtBottomEdge && !forceVisible) {
        lastScrollY = currentY;
        resetDirectionTracking();
        return;
      }

      if (forceVisible || !hideOnScroll) {
        isHidden = false;
        root.classList.remove('is-hidden');
      }

      resetDirectionTracking();
      lastScrollY = currentY;
    };

    const applyScrollState = () => {
      const { currentY, isNearBottom, isAtBottomEdge } = getScrollMetrics();
      const now = getNow();

      if (isMenuOpen) {
        lastScrollY = currentY;
        resetDirectionTracking();
        return;
      }

      if (currentY <= topThreshold) {
        showTopState(currentY, now);
        return;
      }

      showFilledState();

      if (isAtBottomEdge) {
        lastScrollY = currentY;
        resetDirectionTracking();
        return;
      }

      if (!hideOnScroll) {
        isHidden = false;
        root.classList.remove('is-hidden');
        lastScrollY = currentY;
        resetDirectionTracking();
        return;
      }

      const delta = currentY - lastScrollY;
      const absDelta = Math.abs(delta);

      if (absDelta < scrollTolerance) {
        return;
      }

      const direction = delta > 0 ? 1 : -1;

      if (direction !== scrollDirection) {
        scrollDirection = direction;
        directionDistance = 0;
        directionStartedNearBottom = isNearBottom;
      }

      directionDistance += absDelta;

      const thresholds = getThresholds();
      const hasCooldownExpired = now - lastStateChangeTime >= STATE_CHANGE_COOLDOWN;
      const revealThreshold = directionStartedNearBottom || isNearBottom
        ? Math.max(thresholds.reveal, thresholds.footerReveal)
        : thresholds.reveal;

      if (direction > 0) {
        if (!isNearBottom && currentY > revealOffset && directionDistance >= thresholds.hide && hasCooldownExpired) {
          hideHeader(now);
        }
      } else if (directionDistance >= revealThreshold && hasCooldownExpired) {
        showHeader(now);
      }

      lastScrollY = currentY;
    };

    let lastViewportWidth = window.innerWidth;
    let lastViewportHeight = window.innerHeight;
    let resizeFrame = null;

    const onScroll = () => {
      if (isTicking) return;

      isTicking = true;
      window.requestAnimationFrame(() => {
        applyScrollState();
        isTicking = false;
      });
    };

    const onResize = () => {
      if (resizeFrame !== null) return;

      resizeFrame = window.requestAnimationFrame(() => {
        const currentWidth = window.innerWidth;
        const currentHeight = window.innerHeight;
        const isHeightOnlyMobileResize = isMobileViewport() && currentWidth === lastViewportWidth && currentHeight !== lastViewportHeight;

        resizeFrame = null;
        lastViewportWidth = currentWidth;
        lastViewportHeight = currentHeight;

        if (isHeightOnlyMobileResize) {
          debug('header-resize-skipped', 'mobile-height-only');
          return;
        }

        syncScrollState();
      });
    };

    const openMenu = () => {
      if (isMenuOpen) return;

      isMenuOpen = true;
      lastActiveElement = document.activeElement;
      isHidden = false;
      resetDirectionTracking();
      root.classList.remove('is-hidden');
      root.classList.add('is-menu-open', 'is-filled');
      debug('menu-open');
      menu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');

      if ('inert' in menu) {
        menu.inert = false;
      }

      setScrollLock(true);
      window.setTimeout(() => {
        panel.focus({ preventScroll: true });
      }, 80);
    };

    const closeMenu = ({ restoreFocus = true } = {}) => {
      if (!isMenuOpen) return;

      isMenuOpen = false;
      root.classList.remove('is-menu-open');
      debug('menu-close');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');

      if ('inert' in menu) {
        menu.inert = true;
      }

      setScrollLock(false);
      syncScrollState();

      if (restoreFocus && lastActiveElement && typeof lastActiveElement.focus === 'function') {
        lastActiveElement.focus({ preventScroll: true });
      }
    };

    const onKeydown = (event) => {
      if (!isMenuOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(panel);
      if (!focusable.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    toggle.addEventListener('click', openMenu);
    closeControls.forEach((control) => control.addEventListener('click', () => closeMenu()));
    menuLinks.forEach((link) => {
      link.addEventListener('click', () => {
        if (link.hash && link.pathname === window.location.pathname) {
          closeMenu({ restoreFocus: false });
        }
      });
    });
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    syncScrollState({ forceVisible: true });
  };

  const initAll = (container = document) => {
    container.querySelectorAll(HEADER_SELECTOR).forEach(initHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.querySelector(HEADER_SELECTOR);
    if (root) {
      initHeader(root);
      root.classList.add('is-filled');
    }
  });
})();
